// De trendlaag in de oude weergave (dekking eindstand 25-10, punten 2, 4 en 5).
// Alleen gebruikt als de schakelaar aan staat (nieuw/trendlaag/schakelaar.ts);
// uit = de plekken tonen het oude TrendDetail zoals altijd.
//
//  · TrendVanBevinding: een klik op een bevinding (rij uit de oude trends-tabel)
//    toont de trend van de trendlaag. /trendlaag/van vertaalt de bevinding; hoort
//    zij bij geen trend (of mislukt de vraag), dan het oude detail van die
//    bevinding. Zelfde regel als ChatLaagTrend in ChatPaneel.tsx.
//  · LaagTrend: één trend van de trendlaag, los in een paneel of overlay.
//  · BevindingenPerTrendOud: een lijst bevindingen onder hun trend (via
//    /trendlaag/rust); klik op een trend klapt zijn ontwikkelingen open.
//
// De kaart is die van de nieuwe weergave en heeft haar stijl nodig (owner 26-10:
// "dezelfde nette kaart"). Die laadt pas als een LaagTrend op het scherm komt,
// dus alleen bij aan-stand: de bouwstenen, trends.css en trendlaag.css als
// losse import, en de lichte tokens op de kaart zelf (.tl-oud), niet op :root,
// zodat de letter en de achtergrond van het oude scherm blijven zoals ze zijn.

import { useLayoutEffect, useState } from "react";

import { api } from "../lib/api";
import { useResource } from "../lib/useResource";
import { KaartOpDePlek } from "../nieuw/kaartlaag";
// Registreert de kaarten "laagtrend" en "ontwikkeling" (kaart in kaart), en
// "bevinding": een klik op een Waarom nu-regel in de trendkaart.
import "../nieuw/trendlaag/kaart";
import "../nieuw/trends/kaart";
import tokens from "../stijl/tokens.css?inline";
import { type BevindingRef, meervoud, usePerTrend } from "../nieuw/trendlaag/perTrend";
// Kleuren via de chattokens: in de oude weergave dezelfde waarden, in de
// nieuwe volgen ze licht en donker (testfeedback 03-11, rij 6).
import { color, radius, size, weight } from "./chatTokens";
import { TrendDetail } from "./TrendDetail";
import { PanelHeader } from "./ui";

let stijlGeladen = false;

/** De stijl van de nieuwe kaart, één keer, bij de eerste LaagTrend. */
function laadKaartStijl(): void {
  if (stijlGeladen) return;
  stijlGeladen = true;
  // Alleen het eerste :root-blok (de lichte waarden): het oude scherm kent geen donker.
  const licht = tokens.match(/:root\s*\{([^}]*)\}/)?.[1] ?? "";
  const el = document.createElement("style");
  el.dataset.trendlaagOud = "";
  el.textContent =
    `.tl-oud{${licht}color:var(--tekst);font-family:var(--letter);font-size:var(--t-tekst);line-height:var(--regel)}` +
    ".tl-oud *,.tl-oud *::before,.tl-oud *::after{box-sizing:border-box}" +
    ".tl-oud :is(h1,h2,h3,h4,p){margin:0}" +
    ".tl-oud :is(button,input,select,textarea){font:inherit;color:inherit}";
  document.head.appendChild(el);
  void import("../stijl/bouwstenen.css");
  void import("../stijl/entiteit.css");
  void import("../nieuw/trends/trends.css");
  void import("../nieuw/trendlaag/trendlaag.css");
}

/** Eén trend van de trendlaag, met dezelfde kaart als de kaart-in-kaart. Een
 *  klik op een ontwikkeling opent die kaart op dezelfde plek (KaartOpDePlek): een
 *  laag van de kaartlaag zou hier achter het oude paneel of de overlay vallen. */
export function LaagTrend({ trendId, titel, onClose }: { trendId: string; titel?: string; onClose?: () => void }) {
  useLayoutEffect(laadKaartStijl, []);
  return (
    <>
      {onClose && <PanelHeader onClose={onClose} />}
      <div className="tl-oud" style={{ padding: "20px 24px", display: "grid", gap: 12 }} data-testid="laagtrend">
        <KaartOpDePlek
          key={trendId}
          begin={{ soort: "laagtrend", id: trendId, titel }}
          toon={(d, terug, boven) => (
            <>
              {terug && (
                <button
                  type="button"
                  onClick={terug}
                  style={{ all: "unset", cursor: "pointer", fontSize: 12, fontWeight: weight.medium, color: color.muted }}
                >
                  ← Terug
                </button>
              )}
              <div>{d.kop}</div>
              {/* De ontwikkelingen- en de bevindingkaart zetten hun titel zelf bovenaan. */}
              {boven.soort === "laagtrend" && (
                <h2 style={{ margin: 0, fontSize: size.panelTitle, fontWeight: weight.bold, color: color.ink, lineHeight: 1.3 }}>{d.label}</h2>
              )}
              {d.inhoud}
              {d.voet}
            </>
          )}
        />
      </div>
    </>
  );
}

/** Een bevinding openen bij aan-stand: haar trend, of anders haar oude detail. */
export function TrendVanBevinding({ id, onClose }: { id: string; onClose: () => void }) {
  const van = useResource(() => api.trendlaagVan(id), [id]);
  const terugval = !!van.error || (!!van.data && !van.data.trendId);
  const oud = useResource(() => (terugval ? api.trend(id) : Promise.resolve(null)), [id, terugval]);
  if (van.data?.trendId) return <LaagTrend trendId={van.data.trendId} onClose={onClose} />;
  if (oud.data) return <TrendDetail trend={oud.data.trend} onClose={onClose} />;
  if (terugval && oud.error)
    return (
      <>
        <PanelHeader onClose={onClose} />
        <div style={{ padding: 24, fontSize: size.meta, color: color.soft }}>Het detail kon niet worden opgehaald.</div>
      </>
    );
  return <div style={{ padding: 24, fontSize: size.meta, color: color.faint }}>laden…</div>;
}

const rij = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  boxSizing: "border-box",
  background: color.surfaceQuote,
  border: `1px solid ${color.line}`,
  borderRadius: radius.quote,
  padding: "8px 10px",
  fontSize: size.meta,
  color: color.body,
  fontFamily: "inherit",
  textAlign: "left",
} as const;

/** De oude panelen (SkillDetail, SourceDetail) bij aan-stand: bevindingen per trend. */
export function BevindingenPerTrendOud({ bevindingen }: { bevindingen: readonly BevindingRef[] }) {
  const { groepen, los, laden } = usePerTrend(bevindingen);
  const [open, setOpen] = useState<string | null>(null);
  if (laden) return <span style={{ fontSize: size.meta, color: color.faint }}>trends ophalen…</span>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }} aria-label="Bevindingen per trend">
      {groepen.map((g) => (
        <div key={g.trendId}>
          <button
            type="button"
            aria-expanded={open === g.trendId}
            onClick={() => setOpen((o) => (o === g.trendId ? null : g.trendId))}
            style={{ ...rij, cursor: "pointer" }}
          >
            <span style={{ flex: 1, minWidth: 0, fontWeight: weight.medium, color: color.ink }}>{g.titel}</span>
            <span style={{ fontSize: 11, color: color.faint, flexShrink: 0 }}>
              {meervoud(g.bevindingen.length, "bevinding", "bevindingen")}
            </span>
          </button>
          {open === g.trendId && (
            <div style={{ padding: "6px 10px 8px", fontSize: size.meta, color: color.body, display: "grid", gap: 4 }}>
              {g.waaromNu && (
                <div>
                  <span style={{ fontSize: 11, color: color.accentAmber, fontWeight: weight.medium }}>Waarom nu </span>
                  {g.waaromNu}
                </div>
              )}
              {g.ontwikkelingen.map((o) => (
                <div key={o.id}>
                  <b style={{ fontWeight: weight.medium, color: color.ink }}>{o.titel}</b>
                  {o.werkzin && <span style={{ display: "block", color: color.soft }}>{o.werkzin}</span>}
                </div>
              ))}
              <div style={{ color: color.faint, fontSize: 12 }}>{g.bevindingen.map((b) => b.titel).join(" · ")}</div>
            </div>
          )}
        </div>
      ))}
      {los.length > 0 && groepen.length > 0 && <span style={{ fontSize: 11, color: color.faint, marginTop: 4 }}>Nog bij geen trend</span>}
      {los.map((b) => (
        <div key={b.id} style={rij}>
          <span style={{ flex: 1, minWidth: 0 }}>{b.titel}</span>
        </div>
      ))}
    </div>
  );
}
